import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { StaysApiError } from '../../../../api/stays-client';
import {
  deleteMessageTemplate,
  listMessageTemplates,
  MessageLanguage,
  MessageLibrary,
  MessageTemplate,
  restoreDefaultTemplates,
} from '../../../../api/stays-messages-client';
import { AppButton } from '../../../button/app-button';
import { Field, Input, Label } from '../../../form-kit';
import { ConfirmDialog } from '../../../modal/confirm-dialog';
import { FormModal } from '../../../modal/form-modal';
import { SegmentedControl } from '../../../segmented-control/segmented-control';
import { ChipGroup, ToggleChip } from '../../../toggle-chip/toggle-chip';
import { useMediaQuery } from '../../../hooks/use-media-query';
import { Staleable, StateSwitch } from '../../../../motion';
import toast from '../../../../utils/toast';
import { ListSkeleton, LoadError, skeletonHeight } from '../../dashboard/dashboard-shared-components/load-state';
import { refusalText } from '../../dashboard/dashboard-stays-components/stays-labels';
import { up } from '../../../../style-utils/media-queries';
import { bp, layout, palette, radius, shadow, spacing, typography } from '../../../../style-utils/theme';
import { StaysSectionTabs } from '../stays-section';
import { LANGUAGE_OPTIONS } from './message-composer';
import { MessageTemplateEditor } from './message-template-editor';

/**
 * MESSAGE TEMPLATES (plan S8, SC-3; R-S8.20) — /dashboard/stays/messages,
 * stays:manage. The organisation's library in English, Português and
 * Español: the 22 defaults (seeded once by the server) and the host's own.
 * A deleted default STAYS deleted until "Restore default templates" (the
 * Konios re-append is fixed on the server; this page never re-adds one).
 *
 * Desktop: the list beside an inspector. Phone: the list; a tap opens the
 * inspector as a sheet.
 */

const Screen = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
  min-width: 0;
  padding-bottom: ${layout.fabReserve};
`;

const H2 = styled.h2`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.lg}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: ${spacing.sm};
  min-width: 0;
`;

/* The switch and the buttons (44px) centred on the 48px search box. */
const Control = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.sm};
  min-height: ${layout.fieldHeight};
  min-width: 0;
`;

const Search = styled(Field)`
  flex: 1 1 240px;
  max-width: 400px;
  margin: 0;
`;

const Split = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${spacing.md};
  align-items: start;

  ${up('lg')} {
    grid-template-columns: minmax(0, 2fr) minmax(0, 3fr);
  }
`;

const List = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
`;

const Row = styled.button<{ $on: boolean }>`
  appearance: none;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  min-height: ${layout.touchTarget};
  padding: ${spacing.sm};
  text-align: left;
  border: 1px solid ${p => (p.$on ? palette.teal : palette.line)};
  border-radius: ${radius.sm};
  background: ${p => (p.$on ? palette.tealTintSoft : palette.surface)};
  box-shadow: ${shadow.xs};
  font-family: ${typography.ui};
  color: ${palette.ink};
  cursor: pointer;

  &:hover {
    background: ${palette.tealTintSoft};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

const RowTitle = styled.span`
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
`;

const RowMeta = styled.span`
  font-size: ${typography.size.xs}px;
  color: ${palette.slate};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Inspector = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  min-width: 0;
  padding: ${spacing.md};
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.sm};
`;

const Content = styled.p`
  margin: 0;
  padding: ${spacing.sm};
  background: ${palette.surfaceMuted};
  border-radius: ${radius.sm};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.ink};
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

const Meta = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  color: ${palette.slate};
`;

const Buttons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.xs};
`;

const firstLine = (t: string): string => t.split('\n')[0];

export const MessageLibraryPage: React.FC = () => {
  const [language, setLanguage] = useState<MessageLanguage>('en');
  const [lib, setLib] = useState<MessageLibrary | null>(null);
  const [view, setView] = useState<'loading' | 'error' | 'empty' | 'ready'>('loading');
  const [refreshing, setRefreshing] = useState(false);
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('All');
  const [selected, setSelected] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ open: boolean; template: MessageTemplate | null }>({ open: false, template: null });
  const [deleting, setDeleting] = useState<MessageTemplate | null>(null);
  const [restoring, setRestoring] = useState(false);
  const desktop = useMediaQuery(`(min-width: ${bp.lg}px)`);

  const load = useCallback(
    async (quiet = false) => {
      if (quiet) setRefreshing(true);
      else setView('loading');
      try {
        const out = await listMessageTemplates(language);
        setLib(out);
        setView(out.templates.length ? 'ready' : 'empty');
      } catch {
        if (!quiet) setView('error');
      } finally {
        setRefreshing(false);
      }
    },
    [language]
  );

  useEffect(() => {
    setSelected(null);
    setCategory('All');
    void load(false);
  }, [load]);

  const templates = lib ? lib.templates : [];
  const categories = useMemo(() => ['All', ...Array.from(new Set(templates.map(t => t.category)))], [templates]);
  const needle = q.trim().toLowerCase();
  const shown = templates.filter(
    t =>
      (category === 'All' || t.category === category) &&
      (!needle || t.title.toLowerCase().includes(needle) || t.content.toLowerCase().includes(needle))
  );
  const current = templates.find(t => t.id === selected) || (desktop ? shown[0] : undefined) || null;

  const copy = async (t: MessageTemplate) => {
    try {
      await navigator.clipboard.writeText(t.content);
      toast.success('Text copied');
    } catch {
      toast.error('Could not copy. Select the text and copy it yourself.');
    }
  };

  const remove = async () => {
    if (!deleting) return;
    try {
      await deleteMessageTemplate(deleting.id);
      toast.success('Template deleted');
      setDeleting(null);
      setSelected(null);
      await load(true);
    } catch (e) {
      toast.error(refusalText(e as StaysApiError, 'Could not delete the template.'));
    }
  };

  const restore = async () => {
    try {
      const out = await restoreDefaultTemplates(language);
      toast.success(out.restored === 1 ? '1 template restored' : `${out.restored} templates restored`);
      setRestoring(false);
      await load(true);
    } catch (e) {
      toast.error(refusalText(e as StaysApiError, 'Could not restore the templates.'));
    }
  };

  const inspector = (t: MessageTemplate) => (
    <Inspector aria-labelledby={`tpl-${t.id}-title`} data-testid="template-inspector">
      <H2 as="h3" id={`tpl-${t.id}-title`}>
        {t.title}
      </H2>
      <Meta>{t.category}</Meta>
      <Content>{t.content}</Content>
      <Buttons>
        <AppButton type="button" variant="secondary" onClick={() => setEditing({ open: true, template: t })}>
          Edit
        </AppButton>
        <AppButton type="button" variant="secondary" onClick={() => void copy(t)}>
          Copy text
        </AppButton>
        <AppButton type="button" variant="ghost" onClick={() => setDeleting(t)}>
          Delete
        </AppButton>
      </Buttons>
    </Inspector>
  );

  const deleted = lib ? lib.deleted_defaults : 0;

  return (
    <Screen>
      <StaysSectionTabs />
      <H2>Message templates</H2>
      <Toolbar>
        <Control>
          <SegmentedControl<MessageLanguage> label="Language" options={LANGUAGE_OPTIONS} value={language} onChange={setLanguage} />
        </Control>
        <Search>
          <Label htmlFor="msg-search">Search templates</Label>
          <Input id="msg-search" type="search" value={q} maxLength={80} autoComplete="off" onChange={e => setQ(e.target.value)} />
        </Search>
        <Control>
          <AppButton type="button" variant="primary" onClick={() => setEditing({ open: true, template: null })}>
            New template
          </AppButton>
          <AppButton type="button" variant="ghost" disabled={deleted === 0} onClick={() => setRestoring(true)}>
            Restore default templates
          </AppButton>
        </Control>
      </Toolbar>
      <ChipGroup aria-label="Category">
        {categories.map(c => (
          <ToggleChip key={c} pressed={category === c} onToggle={() => setCategory(c)}>
            {c}
          </ToggleChip>
        ))}
      </ChipGroup>
      <StateSwitch state={view} minHeight={skeletonHeight(5, 64)}>
        {view === 'loading' ? <ListSkeleton rows={5} rowHeight={64} label="message templates" /> : null}
        {view === 'error' ? <LoadError message="Couldn't load the message templates" onRetry={() => void load(false)} /> : null}
        {view === 'empty' ? <Meta role="status">No templates yet. Add one with New template.</Meta> : null}
        {view === 'ready' ? (
          <Staleable stale={refreshing}>
            <Split>
              {shown.length ? (
                <List aria-label="Templates">
                  {shown.map(t => (
                    <li key={t.id}>
                      <Row type="button" $on={!!current && current.id === t.id} aria-current={current && current.id === t.id ? 'true' : undefined}
                        onClick={() => setSelected(t.id)}>
                        <RowTitle>{t.title}</RowTitle>
                        {/* One line, ellipsised: the title carries the whole line. */}
                        <RowMeta title={`${t.category} · ${firstLine(t.content)}`}>
                          {t.category} · {firstLine(t.content)}
                        </RowMeta>
                      </Row>
                    </li>
                  ))}
                </List>
              ) : (
                <Meta role="status">No templates match</Meta>
              )}
              {desktop && current ? inspector(current) : null}
            </Split>
          </Staleable>
        ) : null}
      </StateSwitch>

      {!desktop ? (
        <FormModal open={!!selected && !!current} title={current ? current.title : 'Template'} onClose={() => setSelected(null)}>
          {current ? inspector(current) : null}
        </FormModal>
      ) : null}

      <MessageTemplateEditor
        open={editing.open}
        template={editing.template}
        language={language}
        onClose={() => setEditing({ open: false, template: null })}
        onSaved={t => {
          setEditing({ open: false, template: null });
          setSelected(t.language === language ? t.id : null);
          void load(true);
        }}
      />
      <ConfirmDialog
        open={!!deleting}
        title="Delete template"
        message={
          deleting && deleting.default_key
            ? `"${deleting.title}" is removed from the list. You can bring it back with Restore default templates.`
            : `"${deleting ? deleting.title : ''}" is removed for everyone in your organisation.`
        }
        primaryLabel="Delete template"
        primaryVariant="destructive"
        onPrimary={remove}
        onDismiss={() => setDeleting(null)}
      />
      <ConfirmDialog
        open={restoring}
        title={deleted === 1 ? 'Restore 1 deleted template?' : `Restore ${deleted} deleted templates?`}
        message="Templates you edited keep your wording."
        primaryLabel="Restore"
        onPrimary={restore}
        onDismiss={() => setRestoring(false)}
      />
    </Screen>
  );
};
